import type { ApiErrorBody } from '@qualicloud/contracts';
import type { ZodType } from 'zod';
import { ApiError } from './errors';

/**
 * Prefixos de roteamento do gateway (architecture.md §1).
 *
 * O browser conhece UM origin. O gateway é o ponto único de entrada e roteia
 * POR CAMINHO, comparando a URL recebida com a lista branca de
 * `ms-gateway/src/modules/proxy/route-table.ts`. É ele quem injeta o
 * token de serviço em AUDIT e MEDIA — esses dois nunca são chamados
 * diretamente pelo cliente, porque exigem credencial serviço-a-serviço que não
 * pode existir num bundle público.
 *
 * CONVENÇÃO DE PREFIXO — leia antes de mexer.
 *
 * O prefixo declarado aqui é EXATAMENTE o que vai na URL, sem reescrita
 * intermediária nenhuma. Só existe uma transformação no caminho todo:
 * `/api` → `''`, feita pelo proxy do Vite em desenvolvimento e pelo ingress em
 * produção. Depois dela, o que o gateway recebe é este prefixo mais o caminho.
 *
 * Por isso IDENTITY, CORE e MEDIA são STRING VAZIA: o gateway publica
 * `/auth/login`, `/documents` e `/uploads/initiate` na raiz, e um `/identity`
 * ou `/core` a mais faria `matchRoute` comparar 3 segmentos contra 2 e devolver
 * 404 — foi o que derrubou 29 dos 43 passos da jornada. AUDIT, NOTIFICATIONS e
 * BFF mantêm o prefixo porque ele aparece LITERALMENTE no caminho publicado
 * pelo gateway (`/audit/events`, `/notifications/preferences`, `/bff/...`).
 *
 * O escopo continua existindo como DOCUMENTAÇÃO na chamada — quem lê
 * `scope: ApiScope.CORE` sabe com quem está falando —, mas quem decide o
 * roteamento é a tabela do gateway, não o cliente.
 *
 * `BFF` não é um serviço: é o próprio gateway (`ms-gateway/.../bff`), que
 * agrega várias chamadas internas numa só resposta. Existe como escopo separado
 * para deixar explícito, na chamada, que aquele dado NÃO vem de um serviço
 * único — e que a resposta pode vir `degraded` quando um dos serviços
 * secundários não respondeu.
 */
export const ApiScope = {
  IDENTITY: '',
  CORE: '',
  AUDIT: '/audit',
  MEDIA: '',
  NOTIFICATIONS: '/notifications',
  BFF: '/bff',
} as const;
export type ApiScope = (typeof ApiScope)[keyof typeof ApiScope];

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/**
 * A mesma base, exportada para quem monta a URL por fora de `request()` — hoje
 * o envio de arquivo, que usa `XMLHttpRequest` para reportar progresso, e o
 * ticket de download.
 */
export const API_BASE = BASE_URL;

/**
 * Resolve a URL de um ticket contra a base da API.
 *
 * O servidor devolve caminho RELATIVO (`/downloads/:grantId?t=…`) porque não
 * sabe — nem deve saber — sob qual origem ou prefixo o cliente o alcança.
 * Entregue cru a um `window.open`, esse caminho resolveria contra a origem do
 * SPA e traria o `index.html`: o usuário baixaria a página em vez do documento.
 *
 * Enquanto o download era uma URL pré-assinada ABSOLUTA do object storage, o
 * problema não existia — e é por isso que ele aparece agora.
 */
export function toApiUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sessão
// ─────────────────────────────────────────────────────────────────────────────

/**
 * RESTRIÇÃO CRÍTICA DO PROJETO — leia antes de mexer.
 *
 * O access token vive APENAS nesta variável de módulo, em memória. Nunca em
 * `localStorage`, nunca em `sessionStorage`, nunca em cookie legível por JS.
 *
 * Por quê: `localStorage` é acessível a qualquer script na origem, então uma
 * única falha de XSS (ou uma dependência comprometida) exfiltra um token que
 * assina registros eletrônicos com valor legal. Em memória, o token morre com
 * a aba e não é alcançável por `document`.
 *
 * A continuidade da sessão entre recarregamentos vem do REFRESH TOKEN, que o
 * gateway emite como cookie `httpOnly; Secure; SameSite=Strict`. JavaScript não
 * lê esse cookie; ele só viaja porque toda requisição usa `credentials:'include'`.
 * No boot, `bootstrapSession()` troca o cookie por um access token novo.
 */
let accessToken: string | null = null;
let accessTokenExpiresAt: number | null = null;

/** Ouvintes notificados quando a sessão cai (401 irrecuperável). */
type SessionListener = () => void;
const sessionListeners = new Set<SessionListener>();

export function onSessionLost(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

export function setAccessToken(token: string | null, expiresAt?: Date | string | null): void {
  accessToken = token;
  accessTokenExpiresAt = expiresAt ? new Date(expiresAt).getTime() : null;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function hasSession(): boolean {
  return accessToken !== null;
}

export function clearSession(): void {
  accessToken = null;
  accessTokenExpiresAt = null;
}

function notifySessionLost(): void {
  clearSession();
  for (const listener of sessionListeners) listener();
}

/** Margem antes do vencimento para renovar proativamente (30 s). */
const REFRESH_SKEW_MS = 30_000;

function isAccessTokenNearExpiry(): boolean {
  if (accessTokenExpiresAt === null) return false;
  return Date.now() >= accessTokenExpiresAt - REFRESH_SKEW_MS;
}

// ─────────────────────────────────────────────────────────────────────────────
// Renovação
// ─────────────────────────────────────────────────────────────────────────────

interface RefreshResponse {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
}

/**
 * Uma única renovação em voo por vez.
 *
 * Sem esta trava, dez consultas do TanStack Query disparadas juntas fariam dez
 * refreshes concorrentes — e como a rotação de refresh token é obrigatória e
 * reapresentar um token rotacionado REVOGA A FAMÍLIA INTEIRA (auth.controller
 * do identity), isso derrubaria a sessão do usuário na cara dele.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE_URL}${ApiScope.IDENTITY}/auth/refresh`, {
        method: 'POST',
        // O refresh token não vai no corpo: ele é o cookie httpOnly que o
        // gateway lê e repassa ao identity. O corpo vazio é deliberado.
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });

      if (!response.ok) {
        notifySessionLost();
        return false;
      }

      const data = (await response.json()) as RefreshResponse;
      setAccessToken(data.accessToken, data.accessTokenExpiresAt);
      return true;
    } catch {
      notifySessionLost();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Recupera a sessão no carregamento da página.
 * Retorna `true` quando o cookie httpOnly ainda era válido.
 */
export async function bootstrapSession(): Promise<boolean> {
  return refreshAccessToken();
}

// ─────────────────────────────────────────────────────────────────────────────
// Requisição
// ─────────────────────────────────────────────────────────────────────────────

export interface RequestOptions<TResponse> {
  readonly scope: ApiScope;
  readonly path: string;
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly query?: Record<string, unknown> | undefined;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /**
   * Schema Zod de saída. Quando presente, a resposta é PARSEADA e não apenas
   * convertida — é assim que `z.coerce.date()` transforma as strings ISO em
   * `Date` de verdade, e é o mesmo schema que o backend usou para produzir o
   * dado. Divergência de contrato aparece aqui, não três telas adiante.
   */
  readonly responseSchema?: ZodType<TResponse>;
  /** Não tenta renovar em 401. Usado pelo próprio login. */
  readonly skipAuthRefresh?: boolean;
}

export async function request<TResponse = unknown>(
  options: RequestOptions<TResponse>,
): Promise<TResponse> {
  const {
    scope,
    path,
    method = 'GET',
    query,
    body,
    signal,
    responseSchema,
    skipAuthRefresh = false,
  } = options;

  // Renovação proativa: evita a rajada de 401 quando o token vence no meio de
  // uma navegação.
  if (!skipAuthRefresh && accessToken && isAccessTokenNearExpiry()) {
    await refreshAccessToken();
  }

  const url = `${BASE_URL}${scope}${path}${buildQueryString(query)}`;

  const execute = async (): Promise<Response> => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (body !== undefined) headers['content-type'] = 'application/json';

    return fetch(url, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };

  let response: Response;
  try {
    response = await execute();
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw ApiError.network(cause);
  }

  // 401: uma única tentativa de renovar e repetir. Mais que uma vira laço.
  if (response.status === 401 && !skipAuthRefresh) {
    const renewed = await refreshAccessToken();
    if (renewed) {
      try {
        response = await execute();
      } catch (cause) {
        throw ApiError.network(cause);
      }
    } else {
      throw await toApiError(response);
    }
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await response.text()) as TResponse;
  }

  const payload: unknown = await response.json();
  if (!responseSchema) return payload as TResponse;

  const parsed = responseSchema.safeParse(payload);
  if (!parsed.success) {
    // Contrato quebrado entre front e back. Falhar alto é melhor do que
    // renderizar dado inconsistente numa tela regulada.
    throw new ApiError({
      code: 'CLIENT.CONTRACT_MISMATCH',
      status: 500,
      serverMessage: `A resposta de ${method} ${path} não corresponde ao contrato esperado.`,
    });
  }
  return parsed.data;
}

/** Baixa um arquivo (CSV/PDF) devolvendo o `Blob` e o nome sugerido. */
export async function requestFile(options: {
  readonly scope: ApiScope;
  readonly path: string;
  readonly query?: Record<string, unknown>;
  readonly fallbackFileName: string;
}): Promise<{ readonly blob: Blob; readonly fileName: string }> {
  if (accessToken && isAccessTokenNearExpiry()) await refreshAccessToken();

  const url = `${BASE_URL}${options.scope}${options.path}${buildQueryString(options.query)}`;
  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
  } catch (cause) {
    throw ApiError.network(cause);
  }

  if (response.status === 401) {
    const renewed = await refreshAccessToken();
    if (!renewed) throw await toApiError(response);
    const retryHeaders: Record<string, string> = {};
    if (accessToken) retryHeaders.authorization = `Bearer ${accessToken}`;
    response = await fetch(url, { method: 'GET', headers: retryHeaders, credentials: 'include' });
  }

  if (!response.ok) throw await toApiError(response);

  return {
    blob: await response.blob(),
    fileName: fileNameFromDisposition(response.headers.get('content-disposition')) ??
      options.fallbackFileName,
  };
}

/**
 * Envia bytes direto ao armazenamento com URL pré-assinada, reportando
 * progresso. Usa XHR porque `fetch` não expõe progresso de upload — e progresso
 * visível é requisito da tela de nova versão (arquivo de até 20 MB).
 */
/**
 * Envia o arquivo com barra de progresso.
 *
 * `XMLHttpRequest`, e não `fetch`, porque só ele reporta progresso de UPLOAD —
 * `fetch` só expõe progresso de download. Num anexo de 20 MB a diferença é
 * entre uma barra que anda e uma tela parada.
 *
 * O destino é o GATEWAY, não o armazenamento. Antes esta função apontava para
 * uma URL pré-assinada do storage, o que exigia que o navegador o alcançasse —
 * premissa que não se sustentou em cluster, onde o endereço do storage só
 * resolve dentro dele. Como agora a chamada é autenticada como qualquer outra,
 * ela leva o mesmo cabeçalho de sessão e o cookie.
 */
export function uploadWithProgress(params: {
  readonly url: string;
  readonly method: 'PUT';
  readonly file: File;
  readonly onProgress?: (percent: number) => void;
  readonly signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(params.method, params.url, true);
    // O refresh token vive em cookie `HttpOnly`; sem isto o XHR não o envia.
    xhr.withCredentials = true;
    if (accessToken) xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
    // O tipo REAL do arquivo foi declarado na fase 1 e está gravado na sessão.
    // Aqui o corpo é binário opaco — declarar outro tipo criaria uma segunda
    // fonte de verdade para a mesma informação.
    xhr.setRequestHeader('content-type', 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && params.onProgress) {
        params.onProgress((event.loaded / event.total) * 100);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        params.onProgress?.(100);
        resolve();
        return;
      }
      reject(
        new ApiError({
          code: 'MEDIA.UPLOAD_FAILED',
          status: xhr.status,
          serverMessage: 'Falha ao enviar o conteúdo do arquivo.',
        }),
      );
    };

    xhr.onerror = () => reject(ApiError.network());
    xhr.onabort = () => reject(new DOMException('Envio cancelado.', 'AbortError'));

    params.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(params.file);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Auxiliares
// ─────────────────────────────────────────────────────────────────────────────

async function toApiError(response: Response): Promise<ApiError> {
  // Sem inicializador: os dois caminhos abaixo atribuem, e `= null` aqui só
  // seria sobrescrito.
  let body: Partial<ApiErrorBody> | null;
  try {
    body = (await response.json()) as Partial<ApiErrorBody>;
  } catch {
    body = null;
  }
  const error = ApiError.fromBody(response.status, body);
  if (response.status === 401) notifySessionLost();
  return error;
}

export function buildQueryString(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      // Arrays repetem a chave — é o formato que o `zodQuery` do Nest espera.
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        params.append(key, serializeParam(item));
      }
      continue;
    }
    params.append(key, serializeParam(value));
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function serializeParam(value: unknown): string {
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function fileNameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? null;
}

/** Dispara o download de um `Blob` no browser. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
